import React, { useEffect, useRef, useState } from 'react';
import { Check, FileAudio, Mic, MicOff, Trash2, Upload, X } from 'lucide-react';
import type { SampleFile } from '../../lib/talentPlatform';

/* ──────────────────────────────────────────────────────────────
   Talent Form Kit — shared building blocks for the /voice-talent
   application and the portal's in-place profile editor:
   option constants, form primitives (Field/TextInput/Pill), the
   in-browser voice recorder, and the audio upload zone.
   ────────────────────────────────────────────────────────────── */

/* ── Option data ── */
export const LANGUAGES = [
  'Hindi', 'English (Indian)', 'Tamil', 'Telugu', 'Bengali', 'Marathi',
  'Gujarati', 'Kannada', 'Malayalam', 'Punjabi', 'Odia', 'Urdu',
  'Assamese', 'Maithili', 'Sanskrit', 'Konkani', 'Dogri', 'Bodo',
  'Santali', 'Kashmiri', 'Sindhi', 'Nepali', 'Manipuri', 'Other',
];
export const VOICE_GENDERS = ['Male', 'Female', 'Non-binary / Other'];
export const AGE_RANGES = ['18–25', '25–35', '35–50', '50+'];
export const EXPERIENCE_LEVELS = ['Under 1 Year', '1–3 Years', '3–5 Years', '5–10 Years', '10+ Years'];
export const PRO_CATEGORIES = ['Dubbing', 'Narration / Audiobooks', 'Character / Animation', 'Commercials / Ads', 'RJ / Podcasts', 'E-learning / IVR', 'Singing'];
export const COMFORT_OPTIONS = ['Reading scripts aloud', 'Natural free conversation', 'Both'];
export const AVAILABILITY_OPTIONS = ['Weekdays', 'Weekends', 'Evenings only', 'Fully flexible'];
export const HEARD_FROM = ['Instagram', 'LinkedIn', 'WhatsApp', 'A friend / colleague', 'Search', 'Other'];

export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/* Talking prompts — AI training thrives on natural, unscripted monologue,
   so we ask for ~2 minutes of free talk on an easy topic. */
export const RECORD_TOPICS = [
  'Your typical day, start to finish',
  'A dish you love — and how it’s made',
  'The last trip you took',
  'A film or series you’d recommend',
  'What your city is famous for',
  'A childhood memory you still laugh about',
];

/* ── Form primitives (house style) ── */
export const Field = ({ label, optional, children }: { label: string; optional?: boolean; children: React.ReactNode }) => (
  <div className="flex flex-col gap-2">
    <label className="text-[9px] md:text-[clamp(8px,0.64vw,11px)] uppercase tracking-widest text-white/80 font-medium">
      {label}
      {optional && <span className="text-white/40 ml-1 normal-case tracking-normal">(Optional)</span>}
    </label>
    {children}
  </div>
);

export const TextInput = ({ value, onChange, placeholder, type = 'text', error, disabled, hint }: {
  value: string; onChange: (v: string) => void; placeholder: string; type?: string; error?: string; disabled?: boolean; hint?: string;
}) => (
  <div className="flex flex-col gap-1">
    <input
      type={type}
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      className={`w-full bg-white/[0.03] border rounded-xl py-2.5 px-4 text-white text-sm focus:outline-none transition-colors placeholder:text-gray-600 disabled:opacity-60 disabled:cursor-not-allowed ${
        error ? 'border-red-400/60 focus:border-red-400' : 'border-white/10 focus:border-[#56CFFC]/50'
      }`}
    />
    {hint && !error && <span className="text-white/45 text-[10px]">{hint}</span>}
    {error && <span className="text-red-400/90 text-[10px]">{error}</span>}
  </div>
);

export const Pill = ({ selected, onClick, children }: { key?: React.Key; selected: boolean; onClick: () => void; children: React.ReactNode }) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-3.5 py-2 rounded-full text-[11px] md:text-[clamp(10px,0.78vw,13px)] font-semibold transition-all border ${
      selected
        ? 'bg-[#56CFFC]/12 border-[#56CFFC]/60 text-[#56CFFC] shadow-[0_0_14px_rgba(86,207,252,0.18)]'
        : 'bg-transparent border-white/10 text-white/60 hover:text-white hover:border-white/30'
    }`}
  >
    {children}
  </button>
);

/* ══════════════════════════════════════════════════════════════
   Voice recorder — in-browser mic recording with live level bars
   ══════════════════════════════════════════════════════════════ */
export const MAX_RECORD_SECONDS = 120;

const pickMimeType = (): string => {
  if (typeof MediaRecorder === 'undefined') return '';
  for (const t of ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4']) {
    if (MediaRecorder.isTypeSupported(t)) return t;
  }
  return '';
};

export const VoiceRecorder = ({ onRecorded, onClear, sample }: {
  onRecorded: (s: SampleFile) => void;
  onClear: () => void;
  sample: SampleFile | null;
}) => {
  const [phase, setPhase] = useState<'idle' | 'recording' | 'preview'>(sample?.source === 'recorded' ? 'preview' : 'idle');
  const [error, setError] = useState('');
  const [seconds, setSeconds] = useState(0);
  const [levels, setLevels] = useState<number[]>(Array(28).fill(0.12));
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const rafRef = useRef<number>(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  const cleanupMedia = () => {
    cancelAnimationFrame(rafRef.current);
    if (timerRef.current) clearInterval(timerRef.current);
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
    audioCtxRef.current?.close().catch(() => {});
    audioCtxRef.current = null;
  };

  useEffect(() => () => {
    cleanupMedia();
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  // Sync when the parent clears / replaces the sample (e.g. switching to upload)
  useEffect(() => {
    if (!sample && phase === 'preview') {
      setPhase('idle');
      if (previewUrl) { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }
    }
  }, [sample, phase, previewUrl]);

  const start = async () => {
    setError('');
    const mimeType = pickMimeType();
    if (!mimeType || !navigator.mediaDevices?.getUserMedia) {
      setError("Your browser doesn't support in-page recording — please upload a file instead.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
      streamRef.current = stream;

      // Live level bars via AnalyserNode
      const ctx = new AudioContext();
      audioCtxRef.current = ctx;
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 64;
      src.connect(analyser);
      const data = new Uint8Array(analyser.frequencyBinCount);
      const tick = () => {
        analyser.getByteFrequencyData(data);
        const step = Math.floor(data.length / 28) || 1;
        setLevels(Array.from({ length: 28 }, (_, i) => Math.max(0.12, (data[i * step] || 0) / 255)));
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);

      chunksRef.current = [];
      const rec = new MediaRecorder(stream, { mimeType });
      recorderRef.current = rec;
      rec.ondataavailable = e => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType });
        const ext = mimeType.includes('mp4') ? 'm4a' : 'webm';
        const url = URL.createObjectURL(blob);
        setPreviewUrl(prev => { if (prev) URL.revokeObjectURL(prev); return url; });
        setPhase('preview');
        cleanupMedia();
        onRecorded({ blob, name: `recording.${ext}`, source: 'recorded' });
      };
      rec.start();
      setSeconds(0);
      setPhase('recording');
      timerRef.current = setInterval(() => {
        setSeconds(s => {
          if (s + 1 >= MAX_RECORD_SECONDS) stop();
          return s + 1;
        });
      }, 1000);
    } catch {
      setError('Microphone access was blocked. Allow mic access, or upload a file instead.');
      cleanupMedia();
    }
  };

  const stop = () => {
    if (recorderRef.current?.state === 'recording') recorderRef.current.stop();
  };

  const reset = () => {
    if (previewUrl) { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }
    setPhase('idle');
    setSeconds(0);
    onClear();
  };

  const fmt = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  return (
    <div className="flex flex-col gap-3">
      {phase === 'idle' && (
        <button type="button" onClick={start}
          className="w-full flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-white/15 bg-white/[0.02] py-8 md:py-10 hover:border-[#56CFFC]/50 hover:bg-[#56CFFC]/[0.04] transition-all group">
          <span className="w-12 h-12 rounded-full bg-gradient-to-br from-[#56CFFC]/20 to-[#1B36FF]/20 border border-[#56CFFC]/30 flex items-center justify-center text-[#56CFFC] group-hover:scale-110 transition-transform">
            <Mic size={20} />
          </span>
          <span className="text-white/85 text-sm font-semibold">Tap to start recording</span>
          <span className="text-white/60 text-[11px]">Up to 2 minutes — talk naturally, in any language you're applying with</span>
        </button>
      )}

      {phase === 'recording' && (
        <div className="rounded-2xl border border-[#56CFFC]/30 bg-[#56CFFC]/[0.04] p-5 flex flex-col items-center gap-4">
          <div className="flex items-end justify-center gap-[3px] h-12 w-full mask-edges">
            {levels.map((l, i) => (
              <div key={i} className="w-[4px] rounded-full bg-gradient-to-t from-[#1B36FF] to-[#56CFFC] transition-[height] duration-75" style={{ height: `${Math.round(l * 100)}%` }} />
            ))}
          </div>
          <div className="flex items-center gap-3">
            <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
            <span className="text-white font-mono text-sm tabular-nums">{fmt(seconds)} / {fmt(MAX_RECORD_SECONDS)}</span>
          </div>
          <button type="button" onClick={stop}
            className="px-6 py-2.5 rounded-full bg-white text-black text-[10px] font-bold tracking-[0.2em] uppercase hover:scale-105 transition-transform flex items-center gap-2">
            <span className="w-2.5 h-2.5 bg-black rounded-[2px]" /> Stop Recording
          </button>
        </div>
      )}

      {phase === 'preview' && (
        <div className="rounded-2xl border border-white/12 bg-white/[0.03] p-4 flex flex-col gap-3">
          <div className="flex items-center gap-2 text-[#56CFFC] text-[11px] font-bold tracking-widest uppercase">
            <Check size={13} /> Recording captured
          </div>
          {previewUrl && <audio controls src={previewUrl} className="w-full h-9" preload="metadata" />}
          <button type="button" onClick={reset} className="w-fit flex items-center gap-1.5 text-white/60 hover:text-white text-[11px] font-semibold transition-colors">
            <Trash2 size={12} /> Discard & re-record
          </button>
        </div>
      )}

      {error && (
        <p className="flex items-start gap-2 text-amber-300/90 text-[11px] leading-snug"><MicOff size={13} className="shrink-0 mt-0.5" /> {error}</p>
      )}
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════
   Upload zone — drag & drop / tap, with audio preview
   ══════════════════════════════════════════════════════════════ */
export const MAX_UPLOAD_MB = 25;

export const UploadZone = ({ onSelected, onClear, sample }: {
  onSelected: (s: SampleFile) => void;
  onClear: () => void;
  sample: SampleFile | null;
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState('');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  const handleFile = (file: File | undefined | null) => {
    setError('');
    if (!file) return;
    if (!file.type.startsWith('audio/')) { setError('Please choose an audio file (MP3, WAV, M4A…).'); return; }
    if (file.size > MAX_UPLOAD_MB * 1024 * 1024) { setError(`File is too large — max ${MAX_UPLOAD_MB} MB.`); return; }
    const url = URL.createObjectURL(file);
    setPreviewUrl(prev => { if (prev) URL.revokeObjectURL(prev); return url; });
    onSelected({ blob: file, name: file.name, source: 'upload' });
  };

  const clear = () => {
    if (previewUrl) { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }
    if (inputRef.current) inputRef.current.value = '';
    onClear();
  };

  const active = sample?.source === 'upload';

  return (
    <div className="flex flex-col gap-3">
      {!active ? (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={e => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files?.[0]); }}
          className={`w-full flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed py-8 md:py-10 transition-all group ${
            dragOver ? 'border-[#56CFFC] bg-[#56CFFC]/[0.06]' : 'border-white/15 bg-white/[0.02] hover:border-[#56CFFC]/50 hover:bg-[#56CFFC]/[0.04]'
          }`}
        >
          <span className="w-12 h-12 rounded-full bg-gradient-to-br from-[#56CFFC]/20 to-[#1B36FF]/20 border border-[#56CFFC]/30 flex items-center justify-center text-[#56CFFC] group-hover:scale-110 transition-transform">
            <Upload size={19} />
          </span>
          <span className="text-white/85 text-sm font-semibold">Drop your demo here, or tap to browse</span>
          <span className="text-white/60 text-[11px]">MP3, WAV, M4A · up to {MAX_UPLOAD_MB} MB · 30 seconds to 2 minutes is perfect</span>
        </button>
      ) : (
        <div className="rounded-2xl border border-white/12 bg-white/[0.03] p-4 flex flex-col gap-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2.5 min-w-0">
              <FileAudio size={16} className="text-[#56CFFC] shrink-0" />
              <span className="text-white text-sm truncate">{sample!.name}</span>
              <span className="text-white/50 text-[11px] shrink-0">{(sample!.blob.size / (1024 * 1024)).toFixed(1)} MB</span>
            </div>
            <button type="button" onClick={clear} className="text-white/50 hover:text-white transition-colors shrink-0" aria-label="Remove file">
              <X size={15} />
            </button>
          </div>
          {previewUrl && <audio controls src={previewUrl} className="w-full h-9" preload="metadata" />}
        </div>
      )}
      <input ref={inputRef} type="file" accept="audio/*" className="hidden" onChange={e => handleFile(e.target.files?.[0])} />
      {error && <p className="text-amber-300/90 text-[11px]">{error}</p>}
    </div>
  );
};
